import type { Paths } from '@zg-rentals/paths-manifest';

import { Anchor, Box, Text } from '@zillow/constellation-10';
import { getListingAliasFromPath } from '@zg-rentals/string-utils';

import { AdminModal } from '../AdminModal';

import { useBannerVisibility } from '../../hooks/useBannerVisibility';

import { adminBannerStyles } from './styles';

import type { Brand, User } from '../../types';

const USER_INSPECTOR_HOST_NAME = {
  PRODUCTION: 'https://rental-manager-api.hotpads.com',
  COMET1: 'https://comet1.testpads.net',
};

const LISTING_INSPECTOR_HOST_NAME = {
  PRODUCTION: 'https://listing-tools.hotpads.com',
  COMET1: 'https://comet1.testpads.net',
};

type AdminNavProps = {
  brand: Brand;
  userModel: User | null;
  paths: Paths;
  signInPage: string;
  isDevEnv?: boolean;
  pathname: string;
};

export default function AdminNav(props: AdminNavProps) {
  const { isVisible, handleModalConfirm } = useBannerVisibility();

  const { userModel, brand, signInPage, isDevEnv, pathname } = props;
  const { email, impersonatorEmail, impersonatorUserToken, loggedIn, isPostingPathAdmin } = userModel ?? {};
  const isImpersonator = Boolean(impersonatorEmail && impersonatorUserToken && isPostingPathAdmin);
  const userInspectorHostName = isDevEnv ? USER_INSPECTOR_HOST_NAME.COMET1 : USER_INSPECTOR_HOST_NAME.PRODUCTION;
  const listingInspectorHostName = isDevEnv
    ? LISTING_INSPECTOR_HOST_NAME.COMET1
    : LISTING_INSPECTOR_HOST_NAME.PRODUCTION;
  const inspectUserUrl =
    userInspectorHostName +
    '/rental-manager-api/inspectorTool/userInspectorTool?zgBrand=' +
    brand +
    '&userIdentifier=' +
    email +
    '&submitAction=inspectUser';

  const listingId = getListingAliasFromPath(pathname);

  if (!isPostingPathAdmin || !isVisible) {
    return null;
  }
  // If session expired from 30 minutes of inactivity, while impersonating, we need to redirect user to the login page
  // Locally, this will cause an infinite redirect, so only doing this on __PRODUCTION__
  if (isImpersonator && process.env.NODE_ENV === 'production' && !loggedIn) {
    window.location.assign(signInPage);
  }

  let inspectListingUrl;
  if (!listingId) {
    inspectListingUrl = '';
  } else {
    let listingIdQueryParams;
    if (listingId.includes('_')) {
      const feedListingIdAndFeedId = listingId.split('_');
      listingIdQueryParams = 'feedId=' + feedListingIdAndFeedId[1] + '&feedListingId=' + feedListingIdAndFeedId[0];
    } else {
      listingIdQueryParams = 'aliasEncoded=' + listingId;
    }
    inspectListingUrl =
      listingInspectorHostName +
      '/listing-tools/listing/listingInspectorTool?' +
      listingIdQueryParams +
      '&submitAction=inspectListingFromDatabase';
  }

  return (
    <Box data-testid="AdminNav" css={{ height: '38px' }}>
      <Box
        css={{
          backgroundColor: isImpersonator ? 'bg.accent.red.soft' : 'bg.softer',
          borderBottom: isImpersonator
            ? '2px dashed {colors.border.feedback.critical.hero}'
            : '2px dashed {colors.border.express.trust.hero}',
          ...adminBannerStyles,
        }}
      >
        <Text
          textStyle="body-sm-bold"
          id="PostingPathAdminText"
          data-testid="PostingPathAdminText"
          css={{
            color: isImpersonator ? 'text.feedback.critical.hero' : 'text.express.trust.hero',
            paddingRight: '150',
          }}
        >
          {isImpersonator ? 'Impersonating' : 'PostingPathAdmin'}
        </Text>

        <Text textStyle="body-sm">
          <Anchor href={inspectUserUrl} target="_blank" id="Email" data-testid="Email">
            {email}
          </Anchor>{' '}
          |{' '}
        </Text>

        {listingId && (
          <Text textStyle="body-sm">
            <Anchor href={inspectListingUrl} target="_blank">
              {' Inspect Listing '}
            </Anchor>
            |
          </Text>
        )}
        {isImpersonator ? (
          <Text textStyle="body-sm">
            <Anchor
              id="StopImpersonating"
              data-testid="StopImpersonating"
              href={props.paths.stopImpersonatingPage as string}
            >
              Stop Impersonating
            </Anchor>
          </Text>
        ) : (
          <Text textStyle="body-sm">
            <Anchor href={props.paths.impersonatePage}>Impersonate User</Anchor>
          </Text>
        )}
        <AdminModal isImpersonator={isImpersonator} onConfirm={handleModalConfirm} />
      </Box>
    </Box>
  );
}
